import axios, { AxiosPromise } from "axios";
import { env } from "../env/env";
import { ops } from "./config";

// Mirrors the backend YextProfileDto. All fields optional — provided values overlay
// the stored Location data when syncing. Sections post only their own slice.
export interface YextProfileTimeRange {
  start: number; // minutes from midnight
  end: number;
}

export interface YextProfileDayHours {
  open: boolean;
  ranges: YextProfileTimeRange[];
}

export type YextDayKey = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";

export type YextProfileHours = Partial<Record<YextDayKey, YextProfileDayHours>>;

export type YextHolidayMode = "regular" | "closed" | "custom";

// A recurring holiday rule; the backend resolves it to concrete dates when pushing to Yext.
export interface YextHolidayRule {
  key: string;
  label?: string;
  group?: "major" | "big";
  mode: YextHolidayMode;
  start?: number; // minutes from midnight, "custom" only
  end?: number;
}

export interface YextTemporaryClosure {
  from?: string; // ISO "yyyy-MM-dd"
  through?: string;
}

export interface YextProfileLinks {
  onlineOrdering?: string[];
  delivery?: string[];
  reservations?: string[];
  menu?: string[];
  social?: Record<string, string>;
  giftCard?: string[];
}

export interface YextCategory {
  id: string;
  name: string;
  fullName?: string;
}

export interface YextProfileAmenities {
  cuisineTypes?: string;
  serviceOptions?: string[];
  takeout?: string;
  delivery?: string;
  wheelchairAccessible?: string;
  paymentAccepted?: string[];
  reservationSupport?: string;
  orderingLinks?: string[];
  priceRange?: string;
  alcoholServed?: string[];
  outdoorSeating?: string;
  wifiAvailable?: string;
  parking?: string[];
  accessibility?: string[];
  familyFriendly?: string[];
  languages?: string[];
  blackOwned?: string;
}

// One staff bio (Name + Bio text); pushed to Yext as a Bios ECL entry.
export interface YextStaffBio {
  name: string;
  bio: string;
}

// Full hosted image URLs, read-only and server-derived from the location's chosen photos
// (logo falls back to the brand kit's logo when nothing was picked).
export interface YextProfileVisualAssets {
  coverPhoto?: string;
  logo?: string;
  photoGallery?: string[];
}

export interface YextProfile {
  businessName?: string;
  alternateNames?: string;
  category?: string[];
  street?: string;
  street2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  suppressAddress?: boolean;
  displayAddress?: string;
  latitude?: string;
  longitude?: string;
  primaryPhone?: string;
  secondaryPhone?: string;
  email?: string;
  website?: string;
  websiteDisplayUrl?: string;
  descriptionShort?: string;
  descriptionLong?: string;
  featuredMessage?: string;
  video?: string;
  categories?: string[];
  openingDate?: string;
  staffBios?: YextStaffBio[];
  hours?: YextProfileHours;
  holidayHours?: YextHolidayRule[];
  temporaryClosure?: YextTemporaryClosure;
  links?: YextProfileLinks;
  amenities?: YextProfileAmenities;
  visualAssets?: YextProfileVisualAssets;
  // Read-only: false when these values were seeded from the base Location (first-time setup),
  // true when loaded from a previously saved profile.
  existing?: boolean;
}

export interface YextSyncResult {
  entityId: string;
}

// Onboarding gate: whether this location already has a Yext entity.
export type ListingProvider = "YEXT" | "SYNUP";

export interface YextStatus {
  hasEntity: boolean;
  provider: ListingProvider;
  inSetup: boolean;
}

export interface PublishPreview {
  provider: ListingProvider;
  payload: Record<string, unknown>;
  media: Record<string, string[]>;
  missing: string[];
}

export interface YextListingsAccess {
  allowed: boolean;
}

// Draft-vs-live sync state for the header pill.
export interface YextSyncStatus {
  syncedAt?: number; // epoch millis of the last successful transmit to Yext
  liveAt?: number;
  hasUnpublishedChanges: boolean;
  lastError?: string;
  temporaryClosure?: YextTemporaryClosure;
}

// Normalized scan status driving the Status-tab chip.
export type YextListingStatus =
  | "LIVE"
  | "NOT_VERIFIED"
  | "WRONG_PHONE"
  | "INCORRECT_INFO"
  | "NOT_FOUND"
  | "PROCESSING"
  | "OPTED_OUT"
  | "NEEDS_CONNECTION"; // owned publisher (Google/Facebook) awaiting the customer's account link

export interface YextListing {
  publisherId: string;
  publisherName: string;
  status: YextListingStatus;
  rawStatus?: string; // raw Yext status (LIVE, WAITING_ON_*, OPTED_OUT, UNAVAILABLE)
  listingUrl?: string;
  message?: string;
}

// The Status tab payload: managed name/address/phone (shown in every row) + per-publisher rows.
export interface YextListingsView {
  businessName?: string;
  address?: string;
  phone?: string;
  listings: YextListing[];
}

export interface YextPublishResult {
  publisherCount: number;
}

// ---- Reports (analytics) ----
export interface YextReportTotals {
  impressions: number;
  interactions: number;
  profileViews: number;
  searches: number;
}

export interface YextReportPoint {
  date: string; // YYYY-MM-DD
  impressions: number;
  interactions: number;
}

export interface YextPublisherStat {
  publisher: string;
  primaryLabel: string;
  primaryValue: number;
  secondaryLabel?: string;
  secondaryValue?: number;
}

export interface YextReportHealth {
  listingsLive: number;
  accuracy: number; // percent 0–100
  duplicatesSuppressed: number;
}

export interface YextReports {
  connected: boolean;
  rangeDays: number;
  totals: YextReportTotals;
  series: YextReportPoint[];
  publishers: YextPublisherStat[];
  health: YextReportHealth;
}

export interface YextMenuTile {
  id: number;
  designId: string;
  name: string;
  alias?: string | null;
  status: string;
  publishedDate?: string | number | null;
}

export interface YextMenuTileRequest {
  designId: string;
  name: string;
  alias?: string;
}

/** Pulls the backend/Yext error message out of an axios error, falling back to a generic one. */
export function yextErrorMessage(e: any, fallback: string): string {
  return e?.response?.data?.message || fallback;
}

/** Prepends https:// to a bare host so URL fields are valid (mirrors the backend). Blank stays blank. */
export function ensureHttps(url: string): string {
  const v = url.trim();
  if (!v) return "";
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
}

export interface MemberImage {
  id: string;
  name: string;
  path: string;
  source?: string;
  altText?: string;
  isMp4?: boolean;
  isWebM?: boolean;
  isGif?: boolean;
}

export type ListingPhotoSource = "UPLOAD" | "BRAND";

export interface ListingPhoto {
  // Absent for the logo when it falls back to the brand kit (no stored row of its own).
  id?: number;
  slot: string;
  source: ListingPhotoSource;
  imageId?: string;
  imageUrl: string;
  altText?: string;
}

// Cover and logo are singletons (replaced, never cleared); the gallery is a full-state list.
export interface ListingPhotos {
  cover?: ListingPhoto;
  logo?: ListingPhoto;
  gallery: ListingPhoto[];
}

export interface ListingPhotoRequest {
  source: ListingPhotoSource;
  imageId?: string;
  imageUrl: string;
  altText?: string;
}

export function memberImageSrc(image: MemberImage): string {
  const path = image.path || "";
  if (/^https?:\/\//i.test(path)) return path;
  if (path.indexOf("imageservice") > -1) return window.location.origin + path;
  if (image.source === "oo") return `https://mhm-oo.s3.amazonaws.com/${path}`;
  return `https://timber.mhmcdn.com/${path}`;
}

const YextApi = {
  listCategories(): AxiosPromise<YextCategory[]> {
    return axios.get(`${env.apiPath}/yext/categories`, ops());
  },

  /** The locally-stored profile for a location (fields absent if nothing saved yet). */
  getProfile(locationId: number): AxiosPromise<YextProfile> {
    return axios.get(
      `${env.apiPath}/yext/locations/${locationId}/profile`,
      ops()
    );
  },

  listingsAccess(
    memberId: number,
    ownerId: number
  ): AxiosPromise<YextListingsAccess> {
    return axios.get(`${env.apiPath}/yext/listings-access`, {
      ...ops(),
      params: { memberId, ownerId },
    });
  },

  /** Onboarding gate: whether this location already has a Yext entity. */
  getStatus(locationId: number): AxiosPromise<YextStatus> {
    return axios.get(
      `${env.apiPath}/yext/locations/${locationId}/status`,
      ops()
    );
  },

  /** Draft-vs-live sync state (last transmit time, unpublished-changes flag, last error). */
  getSyncStatus(locationId: number): AxiosPromise<YextSyncStatus> {
    return axios.get(
      `${env.apiPath}/yext/locations/${locationId}/sync-status`,
      ops()
    );
  },

  /** Save the working draft locally — no Yext transmit. Autosave calls this. Returns updated status. */
  saveDraft(
    locationId: number,
    profile: YextProfile
  ): AxiosPromise<YextSyncStatus> {
    return axios.post(
      `${env.apiPath}/yext/locations/${locationId}/draft`,
      profile,
      ops()
    );
  },

  /** PUBLISH: transmit the draft (plus any final edits) to Yext and mark it synced. */
  syncLocation(
    locationId: number,
    profile: YextProfile
  ): AxiosPromise<YextSyncResult> {
    return axios.post(
      `${env.apiPath}/yext/locations/${locationId}/sync`,
      profile,
      ops()
    );
  },

  reopen(locationId: number): AxiosPromise<YextSyncResult> {
    return axios.post(
      `${env.apiPath}/yext/locations/${locationId}/reopen`,
      {},
      ops()
    );
  },

  /** Scan view: managed NAP + per-publisher statuses (listings empty until synced). */
  getListings(locationId: number): AxiosPromise<YextListingsView> {
    return axios.get(
      `${env.apiPath}/yext/locations/${locationId}/listings`,
      ops()
    );
  },

  /** Publish the location to all available publishers. */
  publish(locationId: number): AxiosPromise<YextPublishResult> {
    return axios.post(
      `${env.apiPath}/yext/locations/${locationId}/publish`,
      {},
      ops()
    );
  },

  getPublishPreview(locationId: number): AxiosPromise<PublishPreview> {
    return axios.get(
      `${env.apiPath}/yext/locations/${locationId}/publish-preview`,
      ops()
    );
  },

  /** Listings analytics for the Reports tab over the trailing `days` window. */
  getReports(locationId: number, days = 30): AxiosPromise<YextReports> {
    return axios.get(`${env.apiPath}/yext/locations/${locationId}/reports`, {
      ...ops(),
      params: { days },
    });
  },

  // ---- Persisted Home-tab menu tiles ----
  getMenus(locationId: number): AxiosPromise<YextMenuTile[]> {
    return axios.get(
      `${env.apiPath}/yext/locations/${locationId}/menus`,
      ops()
    );
  },
  createMenu(
    locationId: number,
    req: YextMenuTileRequest
  ): AxiosPromise<YextMenuTile> {
    return axios.post(
      `${env.apiPath}/yext/locations/${locationId}/menus`,
      req,
      ops()
    );
  },
  updateMenu(
    locationId: number,
    id: number,
    req: YextMenuTileRequest
  ): AxiosPromise<YextMenuTile> {
    return axios.put(
      `${env.apiPath}/yext/locations/${locationId}/menus/${id}`,
      req,
      ops()
    );
  },
  publishMenu(locationId: number, id: number): AxiosPromise<YextMenuTile> {
    return axios.post(
      `${env.apiPath}/yext/locations/${locationId}/menus/${id}/publish`,
      {},
      ops()
    );
  },
  deleteMenu(locationId: number, id: number): AxiosPromise<void> {
    return axios.delete(
      `${env.apiPath}/yext/locations/${locationId}/menus/${id}`,
      ops()
    );
  },

  // ---- Photos (per-slot listing images + member's Timber uploads) ----
  listMemberImages(memberId: number): AxiosPromise<MemberImage[]> {
    return axios.get(`${env.apiPath}/yext/members/${memberId}/images`, ops());
  },
  uploadMemberImages(
    memberId: number,
    locationId: number,
    files: File[]
  ): AxiosPromise<MemberImage[]> {
    const body = new FormData();
    files.forEach((f) => body.append("images", f));
    body.append("locationId", String(locationId));
    return axios.post(
      `${env.apiPath}/yext/members/${memberId}/images`,
      body,
      ops()
    );
  },
  getPhotos(locationId: number): AxiosPromise<ListingPhotos> {
    return axios.get(
      `${env.apiPath}/yext/locations/${locationId}/photos`,
      ops()
    );
  },
  setPhoto(
    locationId: number,
    slot: "COVER" | "LOGO",
    req: ListingPhotoRequest
  ): AxiosPromise<ListingPhoto> {
    return axios.put(
      `${env.apiPath}/yext/locations/${locationId}/photos/${slot}`,
      req,
      ops()
    );
  },
  addGalleryPhoto(
    locationId: number,
    req: ListingPhotoRequest
  ): AxiosPromise<ListingPhoto> {
    return axios.post(
      `${env.apiPath}/yext/locations/${locationId}/photos/gallery`,
      req,
      ops()
    );
  },
  deleteGalleryPhoto(locationId: number, id: number): AxiosPromise<void> {
    return axios.delete(
      `${env.apiPath}/yext/locations/${locationId}/photos/gallery/${id}`,
      ops()
    );
  },
};

export default YextApi;
